import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Send, Loader2, X, Lightbulb } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

interface AIAssistantProps {
  moduleId: string;
  contextData?: any;
  position?: "inline" | "compact";
  className?: string;
}

export function AIAssistant({ moduleId, contextData, position = "compact", className }: AIAssistantProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);

  const { data: suggestionsData } = useQuery({
    queryKey: ["ai-suggestions", moduleId],
    queryFn: async () => {
      const res = await fetch(`/api/ai/suggestions/${moduleId}`);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: healthData } = useQuery({
    queryKey: ["ai-health"],
    queryFn: async () => {
      const res = await fetch("/api/ai/health");
      return res.json();
    },
    staleTime: 60 * 1000,
  });

  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          moduleId,
          context: contextData,
          conversationHistory: messages.map(m => ({ role: m.role, content: m.content })),
          userId: user?.id,
        }),
      });
      if (!res.ok) throw new Error("Chat failed");
      return res.json();
    },
    onSuccess: (data) => {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: data.response, timestamp: data.timestamp },
      ]);
    },
  });

  const handleSend = () => {
    if (!input.trim() || chatMutation.isPending) return;
    const userMessage: Message = { role: "user", content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    chatMutation.mutate(input);
    setInput("");
  };

  const handleSuggestion = (suggestion: string) => {
    setInput(suggestion);
    setIsOpen(true);
  };

  const isReady = healthData?.status === "ready";

  if (!isReady) return null;

  if (position === "compact" && !isOpen) {
    return (
      <div className={`flex items-center gap-2 ${className || ""}`}>
        <Button variant="outline" size="sm" onClick={() => setIsOpen(true)} className="gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          Asistente IA
        </Button>
        {suggestionsData?.suggestions?.slice(0, 2).map((s: string, i: number) => (
          <Button key={i} variant="ghost" size="sm" onClick={() => handleSuggestion(s)} className="text-xs text-muted-foreground hover:text-foreground">
            <Lightbulb className="w-3 h-3 mr-1" />
            {s.length > 30 ? s.slice(0, 30) + "..." : s}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <Card className={`w-full max-w-md ${className || ""}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            Asistente IA
            <Badge variant="outline" className="text-xs">{moduleId}</Badge>
          </CardTitle>
          {position === "compact" && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsOpen(false)}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {messages.length === 0 && suggestionsData?.suggestions && (
          <div className="flex flex-wrap gap-1">
            {suggestionsData.suggestions.map((s: string, i: number) => (
              <Button key={i} variant="secondary" size="sm" onClick={() => handleSuggestion(s)} className="text-xs h-7">{s}</Button>
            ))}
          </div>
        )}
        {messages.length > 0 && (
          <ScrollArea className="h-48 pr-2">
            <div className="space-y-2">
              {messages.map((m, i) => (
                <div key={i} className={`p-2 rounded-lg text-sm ${m.role === "user" ? "bg-primary text-primary-foreground ml-8" : "bg-muted mr-8"}`}>
                  {m.content}
                </div>
              ))}
              {chatMutation.isPending && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Pensando...
                </div>
              )}
            </div>
          </ScrollArea>
        )}
        <div className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Pregunta algo..." onKeyDown={(e) => e.key === "Enter" && handleSend()} disabled={chatMutation.isPending} className="text-sm" />
          <Button size="icon" onClick={handleSend} disabled={!input.trim() || chatMutation.isPending}>
            {chatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
